import { useState } from 'react';

/**
 * Gesprächsleitfaden — was ein Vertriebspartner im Gespräch griffbereit braucht.
 *
 * WOHER DER INHALT KOMMT
 * Alles hier steht in der Vertriebs- und Marketingstrategie
 * (`marketing/vertriebs-marketing-strategie-20260831.html`, Rafaels Durchsicht
 * vom 02.09.2026) — Einstiegssätze je Produkt (Folie 3), die Eröffnung und die
 * drei Standardantworten (Folie 14), die neun Sprachregeln (Folie 19), der
 * Verkaufsablauf (Folie 9). Diese Folien sind die Quelle; wer dort etwas
 * ändert, ändert es hier mit.
 *
 * WARUM ES NICHT NUR EIN LINK AUF DIE FOLIEN IST
 * Im Gespräch blättert niemand durch 21 Folien. Was hier steht, ist der Teil,
 * den man mitten im Telefonat braucht — in der Reihenfolge, in der er gebraucht
 * wird: was ich sage, was ich antworte, was ich nicht sage.
 *
 * PREISE: die verbindliche Quelle ist werking.tools/preise. Die Zahlen stehen
 * hier trotzdem, weil man sie im Gespräch nennen können muss — mit dem Verweis
 * daneben, damit klar ist, was gilt, wenn beides auseinanderläuft.
 */

type Abschnitt = 'produkte' | 'einstieg' | 'ablauf' | 'antworten' | 'preise' | 'zeitplan' | 'regeln';

const ABSCHNITTE: { value: Abschnitt; label: string }[] = [
  { value: 'produkte', label: 'Produkte' },
  { value: 'einstieg', label: 'Eröffnung' },
  { value: 'ablauf', label: 'Ablauf' },
  { value: 'antworten', label: 'Wenn er fragt…' },
  { value: 'preise', label: 'Preise' },
  { value: 'zeitplan', label: 'Zeitplan' },
  { value: 'regeln', label: 'Was wir nicht sagen' },
];

const KERNSATZ = 'Die KI erstellt den Entwurf — prüfen und freigeben tut der Ingenieur. '
  + 'Und jede Zahl ist nachvollziehbar.';

const EINSTIEGSSAETZE: { produkt: string; rolle: string; satz: string }[] = [
  {
    produkt: 'WerkING Check',
    rolle: 'Türöffner, kein Umsatzprodukt. Kostenlos, ohne Anmeldung, zwei Minuten.',
    satz: 'Wissen Sie, ob Ihr Bericht stimmt?',
  },
  {
    produkt: 'WerkING Report',
    rolle: 'Das Abo-Produkt. Prüfsichere Berichte aus Fotos, Notizen, Messwerten.',
    satz: 'Die KI erstellt den Entwurf. Sie prüfen und geben frei.',
  },
  {
    produkt: 'WerkING Energy',
    rolle: 'Bericht für Bericht, Preis nach Datenlage.',
    satz: 'Der erste Bericht ist gratis — schicken Sie uns ein paar Daten.',
  },
];

const EROEFFNUNG = {
  beiBeziehung: 'Ich arbeite jetzt mit WerkING. Schick mir von einem Objekt die letzten '
    + 'Abrechnungen — du bekommst kostenlos einen Bericht, und dann schauen wir gemeinsam drauf.',
  beiReport: 'Gib mir Projektunterlagen von etwas, das immer nervt.',
  beiNeu: 'Bei neuen Kontakten steht das Erstgespräch davor — telefonisch oder vor Ort, und '
    + 'darin geht es um die Idee dahinter, nicht um Produktdetails. Das Angebot kommt erst danach.',
};

const ANTWORTEN: { frage: string; antwort: string }[] = [
  {
    frage: 'Was kostet mich das?',
    antwort: 'Der erste Bericht nichts — danach stehen die Preise öffentlich auf werking.tools/preise.',
  },
  {
    frage: 'Wer haftet?',
    antwort: 'Sie. Das Werkzeug liefert einen prüfbaren Entwurf, freigeben tut der Ingenieur — '
      + 'genau deshalb funktioniert es.',
  },
  {
    frage: 'Wo landen meine Daten?',
    antwort: 'Sie werden vor der Verarbeitung pseudonymisiert. Mehr versprechen wir bewusst nicht.',
  },
  {
    frage: 'Ich will mir die Software einfach mal anschauen. / Ich hab gerade keine Daten.',
    antwort: 'Gern — aber an fremden Beispieldaten sehen Sie nur Oberfläche. Nehmen Sie irgendein '
      + 'abgeschlossenes Projekt aus dem Archiv, nichts aufbereiten — dann zeige ich Ihnen die Software '
      + 'an Ihrem eigenen Bericht. Für sofort: der Check, zwei Minuten, ohne Anmeldung.',
  },
  {
    frage: 'Ich darf die Unterlagen meines Kunden gar nicht weitergeben.',
    antwort: 'Pseudonymisierung aktiv erklären, AVV griffbereit haben. Ausweichweg anbieten: ein '
      + 'abgeschlossenes Alt-Projekt oder ein anonymisiertes Beispiel.',
  },
];

const ABLAUF: { schritt: string; text: string }[] = [
  { schritt: '1 · Erstgespräch', text: 'Telefonisch oder vor Ort. Die Vision vermitteln, kein Produktdurchlauf.' },
  { schritt: '2 · Das Angebot', text: 'Kein Pitch, ein Angebot: „Geben Sie uns ein Projekt — wir erstellen Ihnen den Bericht."' },
  { schritt: '3 · Er schickt Unterlagen', text: 'Der Filter. Wer schickt, hat Interesse und Zeit — vorher wird keine Zeit investiert. Ab hier ist dein Part zu Ende.' },
  { schritt: '4 · Erstbericht', text: 'WerkING liefert — nie du. Der Kunde bekommt den Bericht plus ein Video, wie er entstanden ist.' },
  { schritt: '5 · Berichtsgespräch', text: 'Die Produktvorstellung: kurz, an SEINEM Bericht, nie abstrakt über Software.' },
  { schritt: '6 · Folgegeschäft', text: 'Report-Abo, bezahlte Berichte, Messkampagne, Portfolio. Was gewollt ist, steht vorher im Trichter.' },
];

const PREISE: { name: string; preis: string; text: string }[] = [
  { name: 'WerkING Check', preis: 'kostenlos', text: 'Ohne Anmeldung. 1 anonymer Lauf ist Absicht, mit Konto 3.' },
  { name: 'WerkING Report', preis: '300 €/Sitz/Monat', text: 'Ein Preis, alle Funktionen, 100 € KI-Budget inkludiert, 7-Tage-Test, der von selbst endet.' },
  { name: 'Energy — Anlagenbericht', preis: '150 €', text: 'Nur Unterlagen: Pläne, Schema, Energieausweis. In Minuten fertig — hier kann jeder Betreiber einsteigen.' },
  { name: 'Energy — Objektbericht', preis: '300 €', text: 'Bewertet das Objekt und listet, was gemessen werden sollte. Daraus entsteht die Messkampagne.' },
  { name: 'Energy — Analysebericht', preis: '600 € (Upgrade +300 €)', text: 'Messdaten, volle Auswertung. Unterlagen des ersten Laufs werden übernommen.' },
];

const REGELN: string[] = [
  'Listenpreise bleiben fix. Struktureller Rabatt ist die Entwicklungspartnerschaft; ein Entgegenkommen im Einzelfall liegt bei dir und wird offen angegeben.',
  'Nie „vollautomatisch". Der Ingenieur prüft und gibt frei — das ist das Verkaufsargument.',
  'Keine Datenschutz-Superlative. Nicht mit „kein US-Transfer" werben. Nur: pseudonymisiert vor der Verarbeitung.',
  'Energy ist kein „Report light". Energy liefert den fertigen Befund; Editor-Wünsche sind ein Report-Upsell, kein fehlendes Feature.',
  'Top-Up nie rabattieren. Das KI-Zusatzbudget hat keinen Rabatt — für niemanden.',
  'Nichts erfinden. Nur Kainer als laufende Referenz. Keine Kundenzahlen, keine „X Büros nutzen das bereits".',
  'Check-Grenzen nicht wegreden. Ein anonymer Lauf ist Absicht. Das Ergebnis ist immer sichtbar, der Export kostet Credits.',
  'Gratis nicht überdehnen. Nur der erste Bericht je Produkt und Unternehmen. Kein Dauerangebot, nicht übertragbar.',
  'Alte Zahlen nicht zitieren. Energy heißt Anlagenbericht / Objektbericht / Analysebericht (150/300/600).',
];



/**
 * Was die Produkte voneinander unterscheidet — die Frage, die im Gespraech als
 * erste kommt und an der man sich blamiert, wenn man sie nicht sauber
 * beantwortet. Abgrenzungen und Reifegrade aus der Vertriebsstrategie
 * (Folien 3, 9, 10) und den Sprachregeln (Folie 19).
 */
const PRODUKTE: {
  name: string;
  einsatz: string;
  fuerWen: string;
  abgrenzung: string;
  stand: string;
  standFarbe: string;
}[] = [
  {
    name: 'WerkING Check',
    einsatz: 'Prüft fertige Unterlagen auf Widersprüche: passt eine Zahl im Bericht nicht zur '
      + 'Aufstellung in der Beilage, meldet er das. Ganze Projektakte hochladen genügt.',
    fuerWen: 'Jeder, der etwas unterschreiben soll und wissen will, ob es stimmt. Kostenlos, ohne '
      + 'Anmeldung — deshalb der Türöffner.',
    abgrenzung: 'Der Check erstellt NICHTS. Er prüft, was schon da ist. Wer einen Bericht erzeugen '
      + 'will, braucht Report oder Energy.',
    stand: 'Seit 1. September öffentlich',
    standFarbe: 'var(--tnf-9ece6a)',
  },
  {
    name: 'WerkING Report',
    einsatz: 'Erzeugt den Bericht: aus Fotos, Notizen und Messwerten wird ein prüfsicherer Entwurf, '
      + 'den der Ingenieur im Editor fertigstellt und freigibt.',
    fuerWen: 'Büros, die laufend Berichte schreiben — Prüfberichte, Gutachten, Dokumentationen. '
      + 'Abo je Sitz, weil es täglich benutzt wird.',
    abgrenzung: 'Report ist das Werkzeug, das der KUNDE bedient. Energy liefert einen fertigen '
      + 'Befund, den er nicht selbst schreibt. Wer den Editor will, will Report.',
    stand: 'Launch Mitte September, Self-Service ab 1. Oktober',
    standFarbe: 'var(--tnf-e0af68)',
  },
  {
    name: 'WerkING Energy',
    einsatz: 'Aus Abrechnungen, Plänen und Messdaten entsteht ein fertiger Energiebericht — '
      + 'Anlagenbericht, Objektbericht oder voller Analysebericht, je nach Datenlage.',
    fuerWen: 'Betreiber und Planer mit Objekten, für die jemand die Energiefrage beantworten soll. '
      + 'Bezahlt wird je Bericht, nicht im Abo.',
    abgrenzung: 'Energy ist kein „Report light". Energy liefert den fertigen Befund; ein Kunde, der '
      + 'selbst schreiben und ändern will, gehört zu Report. Der Einstieg ist der Anlagenbericht, '
      + 'nicht der volle Analysebericht.',
    stand: 'Lieferbar; breiter Verkauf des Analyseberichts nach der Testreihe Ende September',
    standFarbe: 'var(--tnf-e0af68)',
  },
  {
    name: 'WerkING Noise',
    einsatz: 'Schallschutz-Gutachten aus demselben Prüfansatz.',
    fuerWen: 'Bauphysik- und Akustikbüros.',
    abgrenzung: 'Eigenes Produkt, nicht Teil des Report-Abos. Im Vertrieb heuer nachrangig — '
      + 'Report und Energy gehen vor.',
    stand: 'Nicht im Fokus der Ansprachen 2026',
    standFarbe: 'var(--tnf-565f89)',
  },
];

/**
 * Der Fahrplan bis Jahresende (Strategie-Folien 10 und 11) und der Zielrahmen
 * fuer die eigenen Ansprachen (Folie 13). Nicht als Zierde: an diesen Terminen
 * haengt, wann welche Ansprache Sinn hat.
 */
const CHECKPOINTS: { wann: string; was: string; woran: string }[] = [
  {
    wann: 'Anfang September',
    was: 'Abstimmung Rafael + Markus: Kandidatenliste, Kontakt-Zuordnung, Plattform, Vertragspunkte. '
      + 'Parallel erste Concierge-Ansprachen auf die A-Leads durch Rafael.',
    woran: 'Zuordnung steht schriftlich, erste Unterlagen treffen ein',
  },
  {
    wann: 'Mitte September',
    was: 'Report-Launch öffentlich: Ankündigung und Concierge-Angebot an die A-Liste — gestaffelt, '
      + 'nur so viele Ansprachen wie Erstberichte lieferbar sind.',
    woran: 'laufende Concierge-Fälle mit notiertem Folgegeschäft',
  },
  {
    wann: 'Ende September',
    was: 'Zwei Abnahmen: die Energy-Testreihe meldet „massenfähig" oder nicht, und die Case Study '
      + 'Kainer ist fertig. Dazu die Demo-Bibliothek aus synthetischen Fällen.',
    woran: 'Testprotokoll grün, Case Study freigegeben',
  },
  {
    wann: '1. Oktober',
    was: 'Report Self-Service geht online. Markus startet seine ersten Ansprachen; erste '
      + 'Erstgespräche über gelieferte Berichte.',
    woran: 'erste Käufe ohne Gespräch, Erstgespräche finden statt',
  },
  {
    wann: 'Ende November',
    was: 'Kanal-Entscheidung an den Check-Zahlen: mehr Budget in den Online-Weg oder volle Kraft '
      + 'in den Direktvertrieb.',
    woran: 'Entscheidung dokumentiert, Budget umgeschichtet',
  },
  {
    wann: 'Dezember',
    was: 'Folgeaufträge aus den Erstberichten heben, gemeinsamer Jahresrückblick, Planung 2027.',
    woran: 'erste wiederkehrende Umsätze stehen',
  },
];

const ZIELRAHMEN: { monat: string; ansprachen: string; unterlagen: string; gespraeche: string; auftraege: string }[] = [
  { monat: 'Oktober', ansprachen: '8 (2/Woche)', unterlagen: '~4', gespraeche: '~3–4', auftraege: '0–1' },
  { monat: 'November', ansprachen: '12 (3/Woche)', unterlagen: '~6', gespraeche: '~5–6', auftraege: '1–3' },
  { monat: 'Dezember', ansprachen: '12 + Folgetermine', unterlagen: '~6', gespraeche: '~5–6', auftraege: '3–6 bis Ende Jänner' },
];

// ─── Bausteine ───────────────────────────────────────────────────────────────

function Karte({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      padding: '14px 16px', borderRadius: 8, marginBottom: 10,
      background: 'var(--tn-surface)', border: '1px solid var(--tn-border)',
    }}>
      {children}
    </div>
  );
}

function Zitat({ text }: { text: string }) {
  const [kopiert, setKopiert] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{
        flex: 1, fontSize: 14, lineHeight: 1.6, color: 'var(--tn-text)',
        borderLeft: '3px solid var(--tn-blue)', paddingLeft: 12,
      }}>
        „{text}"
      </div>
      <button
        data-ai-id="sales-leitfaden-kopieren"
        onClick={() => {
          navigator.clipboard.writeText(text)
            .then(() => { setKopiert(true); setTimeout(() => setKopiert(false), 1800); })
            .catch(err => console.error('[Leitfaden] Kopieren fehlgeschlagen:', err));
        }}
        style={{
          padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', whiteSpace: 'nowrap',
          background: 'var(--tn-bg)', color: 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
        }}
      >
        {kopiert ? 'kopiert' : 'kopieren'}
      </button>
    </div>
  );
}

// ─── Panel ───────────────────────────────────────────────────────────────────

export default function SalesLeitfadenPanel() {
  const [abschnitt, setAbschnitt] = useState<Abschnitt>('einstieg');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--tn-bg)' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', flexWrap: 'wrap',
        borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
      }}>
        {ABSCHNITTE.map(a => (
          <button
            key={a.value}
            data-ai-id={`sales-leitfaden-${a.value}`}
            onClick={() => setAbschnitt(a.value)}
            style={{
              padding: '6px 14px', borderRadius: 5, fontSize: 12, cursor: 'pointer',
              fontWeight: abschnitt === a.value ? 700 : 500,
              background: abschnitt === a.value ? 'var(--tn-blue)' : 'var(--tn-surface)',
              color: abschnitt === a.value ? '#fff' : 'var(--tn-text)',
              border: '1px solid var(--tn-border)',
            }}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 20, maxWidth: 900 }}>
        {abschnitt === 'produkte' && (
          <>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
              Alle drei folgen derselben Idee: die KI erstellt den Entwurf, der Ingenieur prüft und
              gibt frei. Der Unterschied liegt darin, WER am Ende schreibt und WOFÜR bezahlt wird.
            </div>
            {PRODUKTE.map(p => (
              <Karte key={p.name}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'baseline', marginBottom: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tn-text)', flex: 1 }}>{p.name}</div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: p.standFarbe, whiteSpace: 'nowrap' }}>
                    {p.stand}
                  </div>
                </div>
                <div style={{ fontSize: 13, color: 'var(--tn-text)', lineHeight: 1.6, marginBottom: 8 }}>
                  {p.einsatz}
                </div>
                <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6, marginBottom: 6 }}>
                  <strong style={{ color: 'var(--tn-text)' }}>Für wen: </strong>{p.fuerWen}
                </div>
                <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--tnf-e0af68)' }}>Abgrenzung: </strong>{p.abgrenzung}
                </div>
              </Karte>
            ))}
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6 }}>
              Als Einstieg ist Report meist klüger als Energy — Energy kostet je Lauf deutlich mehr.
              Wenn ein Kunde beides will, nacheinander: dann hat er zwei Beispiele in der Hand.
            </div>
          </>
        )}

        {abschnitt === 'einstieg' && (
          <>
            <Karte>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Die Kernbotschaft — der Satz, auf den alles zurückläuft
              </div>
              <Zitat text={KERNSATZ} />
            </Karte>

            <Karte>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 6 }}>
                Eröffnung bei jemandem, den du kennst
              </div>
              <Zitat text={EROEFFNUNG.beiBeziehung} />
              <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 10 }}>
                Für Report sinngemäß: „{EROEFFNUNG.beiReport}"
              </div>
              <div style={{ fontSize: 12, color: 'var(--tnf-e0af68)', marginTop: 8, lineHeight: 1.5 }}>
                {EROEFFNUNG.beiNeu}
              </div>
            </Karte>

            {EINSTIEGSSAETZE.map(e => (
              <Karte key={e.produkt}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)' }}>{e.produkt}</div>
                <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 8 }}>{e.rolle}</div>
                <Zitat text={e.satz} />
              </Karte>
            ))}

            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6, marginTop: 4 }}>
              Versprochen wird ein kostenloser Erstbericht aus vorhandenen Daten und ein Gespräch
              darüber. Sonst nichts — keine Lieferzeit, keine Vollautomatik.
            </div>
          </>
        )}

        {abschnitt === 'ablauf' && (
          <>
            {ABLAUF.map(a => (
              <Karte key={a.schritt}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-blue)', marginBottom: 4 }}>{a.schritt}</div>
                <div style={{ fontSize: 13, color: 'var(--tn-text)', lineHeight: 1.6 }}>{a.text}</div>
              </Karte>
            ))}
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6 }}>
              Gebraucht wird — Energy: Abrechnungen oder Zählerablesungen eines Objekts, Pläne und
              Energieausweis falls vorhanden. Report: Unterlagen eines nervigen Projekts und ein
              Beispiel des Zielberichts. Immer dazusagen: nichts aufbereiten.
            </div>
          </>
        )}

        {abschnitt === 'antworten' && ANTWORTEN.map(a => (
          <Karte key={a.frage}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 6 }}>
              „{a.frage}"
            </div>
            <div style={{ fontSize: 13, color: 'var(--tn-text)', lineHeight: 1.6 }}>{a.antwort}</div>
          </Karte>
        ))}

        {abschnitt === 'preise' && (
          <>
            {PREISE.map(p => (
              <Karte key={p.name}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'baseline' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)', flex: 1 }}>{p.name}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tnf-9ece6a)', whiteSpace: 'nowrap' }}>{p.preis}</div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 4, lineHeight: 1.5 }}>{p.text}</div>
              </Karte>
            ))}
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6 }}>
              Verbindlich ist die öffentliche Preisseite —{' '}
              <a href="https://werking.tools/preise" target="_blank" rel="noreferrer" style={{ color: 'var(--tn-blue)' }}>
                werking.tools/preise
              </a>
              . Wenn eine Zahl hier anders aussieht als dort, gilt dort. Der erste Bericht je Produkt
              und Unternehmen ist gratis; das beendet auch jede Preisverhandlung freundlich.
            </div>
          </>
        )}

        {abschnitt === 'zeitplan' && (
          <>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
              Warum das Marketing vor dem fertigen Produkt startet: zwischen erster Vorstellung und
              Auftrag liegen sechs bis zwölf Wochen. Wer erst mit dem fertigen Produkt zu werben
              beginnt, steht mit leerem Trichter da. September und Oktober füllen den Trichter,
              November und Dezember schließen ab.
            </div>

            {CHECKPOINTS.map(c => (
              <Karte key={c.wann}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-blue)', marginBottom: 4 }}>{c.wann}</div>
                <div style={{ fontSize: 13, color: 'var(--tn-text)', lineHeight: 1.6 }}>{c.was}</div>
                <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 6 }}>
                  Woran wir merken, dass es geklappt hat: {c.woran}
                </div>
              </Karte>
            ))}

            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--tn-text)', margin: '20px 0 8px' }}>
              Dein Zielrahmen
            </div>
            <Karte>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ color: 'var(--tn-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px 8px 0' }}>Monat</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px 8px 0' }}>Ansprachen</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px 8px 0' }}>Unterlagen</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px 8px 0' }}>Erstgespräche</th>
                    <th style={{ textAlign: 'left', padding: '4px 0 8px 0' }}>Aufträge</th>
                  </tr>
                </thead>
                <tbody>
                  {ZIELRAHMEN.map(z => (
                    <tr key={z.monat}>
                      <td style={{ padding: '5px 8px 5px 0', fontWeight: 600, color: 'var(--tn-text)' }}>{z.monat}</td>
                      <td style={{ padding: '5px 8px 5px 0', color: 'var(--tn-text)' }}>{z.ansprachen}</td>
                      <td style={{ padding: '5px 8px 5px 0', color: 'var(--tn-text-muted)' }}>{z.unterlagen}</td>
                      <td style={{ padding: '5px 8px 5px 0', color: 'var(--tn-text-muted)' }}>{z.gespraeche}</td>
                      <td style={{ padding: '5px 0', color: 'var(--tnf-9ece6a)' }}>{z.auftraege}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Karte>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', lineHeight: 1.6 }}>
              Die Rechnung dahinter: aus rund sechs bis acht Erstansprachen wird ein Auftrag. Unter
              acht Ansprachen im Monat liefert die Kette im Jänner statistisch null bis einen Auftrag
              — genau der leere Trichter, den es zu vermeiden gilt. Mehr geht auch nicht: die
              Lieferkapazität für Erstberichte liegt bei etwa fünf bis sechs im Monat.
            </div>
          </>
        )}

        {abschnitt === 'regeln' && (
          <>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
              Neun Regeln, die für alle Gespräche und alle Texte gelten — unabhängig davon, wer sie führt.
            </div>
            {REGELN.map((r, i) => (
              <Karte key={i}>
                <div style={{ display: 'flex', gap: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--tn-blue)' }}>{i + 1}</div>
                  <div style={{ fontSize: 13, color: 'var(--tn-text)', lineHeight: 1.6 }}>{r}</div>
                </div>
              </Karte>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
